import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Sparkles, Save, RefreshCw, Loader2 } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

/**
 * Studio admin panel — surface every product reference with its Gemini Vision
 * description, let Kaily edit it inline, and provide a "Refresh AI" button to
 * re-run Vision on every product. This is the SAFETY NET so reels / images
 * never get rendered with the wrong product shape.
 */
export default function ProductVisionPanel({ order, onReload, studioHeaders }) {
    const refs = (order?.intake?.product_refs) || [];
    const [refreshing, setRefreshing] = useState(false);

    if (!refs.length) {
        return (
            <div
                className="rounded-2xl border border-dashed border-cafe-mocha/30 bg-cafe-cream/50 p-5"
                data-testid="product-vision-empty"
            >
                <p className="text-xs uppercase tracking-[0.18em] text-cafe-mocha font-bold mb-1">Product Vision</p>
                <p className="text-sm text-cafe-mocha">
                    No product references on this order. Upload product photos via the intake flow first.
                </p>
            </div>
        );
    }

    const refreshAll = async () => {
        if (!window.confirm(`Re-run Gemini Vision on all ${refs.length} product photos? Current descriptions will be replaced.`)) return;
        setRefreshing(true);
        try {
            const { data } = await axios.post(
                `${API}/orders/${order.id}/refresh_product_descriptions`,
                {},
                { headers: studioHeaders() },
            );
            toast.success(`Re-reading ${data?.refreshing || refs.length} product photos…`, {
                description: "Refresh the page in 60s to see the new descriptions.",
                duration: 6000,
            });
            // Soft reload after delay
            setTimeout(() => { onReload && onReload(); }, 70000);
        } catch (err) {
            const msg = err?.response?.data?.detail || "Failed to refresh";
            toast.error(msg);
        } finally {
            setRefreshing(false);
        }
    };

    return (
        <div className="rounded-2xl border border-border bg-cafe-cream p-5" data-testid="product-vision-panel">
            <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
                <div>
                    <p className="text-[10px] uppercase tracking-[0.18em] text-cafe-mocha font-bold mb-0.5 flex items-center gap-1.5">
                        <Sparkles className="w-3 h-3 text-cafe-olive" /> Product Vision
                    </p>
                    <p className="font-serif text-lg text-cafe-espresso">
                        How AI sees {refs.length} product{refs.length > 1 ? "s" : ""}
                    </p>
                    <p className="text-xs text-cafe-mocha mt-1 max-w-md leading-relaxed">
                        These descriptions are injected into every reel + image prompt to lock the product's exact shape. Edit any line if it's not accurate.
                    </p>
                </div>
                <Button
                    onClick={refreshAll}
                    disabled={refreshing}
                    data-testid="refresh-all-vision-btn"
                    variant="outline"
                    size="sm"
                    className="rounded-full border-cafe-olive/60 bg-transparent text-cafe-olive hover:bg-cafe-olive/10 h-9 px-3 text-xs"
                >
                    {refreshing ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1.5" />}
                    Refresh all (Gemini)
                </Button>
            </div>

            <div className="space-y-4">
                {refs.map((p, idx) => (
                    <ProductRow key={idx} order={order} product={p} idx={idx} studioHeaders={studioHeaders} onReload={onReload} />
                ))}
            </div>
        </div>
    );
}

function ProductRow({ order, product, idx, studioHeaders, onReload }) {
    const [desc, setDesc] = useState(product.visual_description || "");
    const [saving, setSaving] = useState(false);
    const dirty = desc.trim() !== (product.visual_description || "").trim();

    const save = async () => {
        if (!dirty || !desc.trim()) return;
        setSaving(true);
        try {
            await axios.put(
                `${API}/orders/${order.id}/product_description`,
                { product_index: idx, visual_description: desc.trim() },
                { headers: studioHeaders() },
            );
            toast.success(`${product.name || "Product"} description saved`, {
                description: "Future reel/image renders will use this exact description.",
            });
            onReload && onReload();
        } catch (err) {
            const msg = err?.response?.data?.detail || "Save failed";
            toast.error(msg);
        } finally {
            setSaving(false);
        }
    };

    const imgUrl = product.image_url ? (product.image_url.startsWith("http") ? product.image_url : `${BACKEND}${product.image_url}`) : null;

    return (
        <div className="grid grid-cols-1 sm:grid-cols-[120px_1fr] gap-4 p-3 rounded-xl bg-cafe-latte/30">
            <div>
                <p className="text-[10px] uppercase tracking-[0.15em] text-cafe-mocha font-bold mb-1.5">
                    {product.name || `Product ${idx + 1}`}
                </p>
                {imgUrl ? (
                    <a
                        href={imgUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block w-full aspect-square rounded-lg overflow-hidden bg-cafe-espresso/10 hover:opacity-80 transition-opacity"
                        title="Click to open photo"
                    >
                        <img src={imgUrl} alt={product.name || "product"} className="w-full h-full object-cover" loading="lazy" />
                    </a>
                ) : (
                    <div className="w-full aspect-square rounded-lg bg-cafe-espresso/10 flex items-center justify-center text-[10px] text-cafe-mocha">
                        No photo
                    </div>
                )}
            </div>
            <div className="flex flex-col gap-2">
                <Textarea
                    value={desc}
                    onChange={e => setDesc(e.target.value)}
                    rows={5}
                    placeholder="(no description yet — click Refresh all above)"
                    data-testid={`product-desc-${idx}`}
                    className="text-xs leading-relaxed bg-cafe-cream font-mono"
                />
                <div className="flex items-center justify-between gap-2 flex-wrap">
                    <p className="text-[10px] text-cafe-mocha/70">
                        {desc.length} chars · {desc.trim().split(/\s+/).filter(Boolean).length} words
                    </p>
                    <Button
                        onClick={save}
                        disabled={!dirty || saving || !desc.trim()}
                        size="sm"
                        data-testid={`product-save-${idx}`}
                        className="rounded-full bg-cafe-olive hover:bg-cafe-oliveDark text-cafe-cream h-8 px-3 text-xs disabled:opacity-50"
                    >
                        {saving ? <Loader2 className="w-3 h-3 mr-1.5 animate-spin" /> : <Save className="w-3 h-3 mr-1.5" />}
                        {dirty ? "Save edits" : "Saved"}
                    </Button>
                </div>
            </div>
        </div>
    );
}
